/**
 * @description
 * Este arquivo cria diferentes instâncias do axios para cada um dos backends utilizados pelo painel.
 * Este arquivo também define os endpoints (rotas) dos diferentes backends utilizados pelo painel.
 *
 * As rotas descritas no arquivo `src/routes/paths.ts` são referentes aos "caminhos de URL" do frontend
 * (paths), que serão utilizados em outros arquivos para navegação via react-router.
 */

import axios, { AxiosRequestConfig, AxiosInstance, AxiosError } from 'axios';

import { APP_API_URL, STORAGE_KEY_ACCESS, STORAGE_KEY_REFRESH } from 'src/config-global';

// Variável para controlar se já está renovando o token
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Factory function para criar instâncias axios padronizadas
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    (config) => {
      // Se for FormData, não sobrescreva o Content-Type
      // O browser vai setar automaticamente com o boundary correto
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
      return { ...config };
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const originalRequest: any = error.config;

      // Se for erro 401 (token expirado/inválido) e não for a rota de refresh ou login
      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/refresh')
      ) {
        if (isRefreshing) {
          // Se já está renovando, adiciona na fila
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH);

        if (!refreshToken) {
          // Se não tem refresh token, desloga o usuário
          processQueue(error, null);
          isRefreshing = false;

          // Limpa a sessão e redireciona para login
          localStorage.removeItem(STORAGE_KEY_ACCESS);
          localStorage.removeItem(STORAGE_KEY_REFRESH);
          window.location.href = '/auth/login';

          return Promise.reject(error);
        }

        try {
          // Tenta renovar o token
          const response = await instance.post('/backoffice/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          // Salva os novos tokens
          localStorage.setItem(STORAGE_KEY_ACCESS, accessToken);
          if (newRefreshToken) {
            localStorage.setItem(STORAGE_KEY_REFRESH, newRefreshToken);
          }

          // Atualiza o header da instância
          instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // Dispara evento customizado para notificar que o token foi atualizado
          window.dispatchEvent(new CustomEvent('token-refreshed', { detail: { accessToken } }));

          // Processa a fila de requisições que falharam
          processQueue(null, accessToken);
          isRefreshing = false;

          // Refaz a requisição original
          return await instance(originalRequest);
        } catch (refreshError) {
          // Se falhar ao renovar, desloga o usuário
          processQueue(refreshError, null);
          isRefreshing = false;

          // Limpa a sessão e redireciona para login
          localStorage.removeItem(STORAGE_KEY_ACCESS);
          localStorage.removeItem(STORAGE_KEY_REFRESH);
          window.location.href = '/auth/login';

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject((error.response && error.response.data) || 'Something went wrong');
    }
  );

  return instance;
};

export type axiosInstanceType = 'axiosInstance';

// Criar as instâncias
export const axiosInstance = createAxiosInstance(APP_API_URL);

// Factory function para criar fetchers
const createFetcher = (instance: AxiosInstance) => async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await instance.get(url, { ...config });
  return res.data;
};

// Criar os fetchers
export const appFetcher = createFetcher(axiosInstance);

// Endpoints da App API
export const endpoints = {
  backoffice: {
    auth: {
      login: '/backoffice/auth/login',
      refresh: '/backoffice/auth/refresh',
      logout: '/backoffice/auth/logout',
    },
    admin: {
      list: '/backoffice/admin',
      create: '/backoffice/admin',
      update: (id: string) => `/backoffice/admin/${id}`,
    },
    client: {
      list: '/client',
      create: '/client/create',
      update: (id: string) => `/client/${id}`,
    },
    clientAdmins: {
      list: '/backoffice/client-admins',
      create: '/backoffice/client-admins',
      getById: (id: string) => `/backoffice/client-admins/${id}`,
      update: (id: string) => `/backoffice/client-admins/${id}`,
    },
    files: {
      upload: '/files/upload',
    },
  },
};
